import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import ProductsPage from '../components/ProductsPage';
import ProductDetailPage from '../components/ProductDetailPage';
import CartPage from '../components/CartPage';
import Profile from '../components/Profile';
import AdminPanel from '../components/AdminPanel';
import SharedPageLayout from '../components/SharedPageLayout';
import { Settings, CheckCircle, Zap, Layers, Star, Users, PencilRuler, ArrowRight, Target } from 'lucide-react';
import { BACKEND_URL } from '../config';

// Utility function to create SEO-friendly slugs (needed by ProductsRoute)
const createProductSlug = (manufacturer, productName) => {
  const combined = `${manufacturer} ${productName}`;
  return combined
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim('-');
};

// Utility function to parse slug back to search terms
const parseProductSlug = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};


export const ProductsRoute = () => {
  const navigate = useNavigate();
  const { cart, addToCart } = useCart();

  const navigateHome = () => navigate('/');
  const navigateToProduct = (product) => {
    const slug = createProductSlug(product.manufacturer, product.name);
    navigate(`/product/${slug}`);
  };
  const navigateToCart = () => navigate('/cart');
  const navigateToProfile = () => navigate('/profile');

  return (
    <ProductsPage 
      navigateHome={navigateHome} 
      navigateToProduct={navigateToProduct} 
      navigateToCart={navigateToCart} 
      navigateToProfile={navigateToProfile}
      cart={cart} 
      addToCart={addToCart} 
    />
  );
};

export const ProductDetailRoute = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { cart, addToCart } = useCart();
  const [productId, setProductId] = useState(null);

  // Extract product info from slug and find the actual product
  useEffect(() => {
    const findProductBySlug = async () => {
      try {
        // Parse the slug to get search terms for the API search
        const searchTerms = parseProductSlug(slug);
        
        // Fetch products with a large limit to ensure we find the product
        // Use search terms from slug to narrow down results
        const params = new URLSearchParams({
          per_page: '500',  // Get all products to ensure we find the match
          search: searchTerms  // Use search to help find the product
        });
        
        const response = await fetch(`${BACKEND_URL}/api/products?${params}`);
        const data = await response.json();
        
        // Find product that matches the slug exactly
        const matchingProduct = data.products.find(product => {
          const productSlug = createProductSlug(product.manufacturer, product.name);
          return productSlug === slug;
        });
        
        if (matchingProduct) {
          setProductId(matchingProduct.id);
        } else {
          // If no exact match with search, try fetching all products as fallback
          const fallbackResponse = await fetch(`${BACKEND_URL}/api/products?per_page=500`);
          const fallbackData = await fallbackResponse.json();
          
          const fallbackMatch = fallbackData.products.find(product => {
            const productSlug = createProductSlug(product.manufacturer, product.name);
            return productSlug === slug;
          });
          
          if (fallbackMatch) {
            setProductId(fallbackMatch.id);
          } else {
            // If still no match, redirect to products page
            console.warn(`Product not found for slug: ${slug}`);
            navigate('/products');
          }
        }
      } catch (error) {
        console.error('Error finding product:', error);
        navigate('/products');
      }
    };

    if (slug) {
      findProductBySlug();
    }
  }, [slug, navigate]);

  const navigateToProducts = () => navigate('/products');
  const navigateHome = () => navigate('/');
  const navigateToCart = () => navigate('/cart');

  // Don't render until we have the productId
  if (!productId) {
    return <div>Loading...</div>;
  }

  return (
    <ProductDetailPage 
      productId={productId} 
      navigateToProducts={navigateToProducts} 
      navigateHome={navigateHome} 
      navigateToCart={navigateToCart} 
      addToCart={addToCart} 
      cart={cart} 
    />
  );
};

export const CartRoute = () => {
  const navigate = useNavigate();
  const { cart, setCart, clearCart } = useCart();

  const navigateHome = () => navigate('/');
  const navigateToProducts = () => navigate('/products');

  return (
    <CartPage 
      navigateHome={navigateHome} 
      navigateToProducts={navigateToProducts} 
      cart={cart} 
      setCart={setCart}
      clearCart={clearCart}
    />
  );
};

export const ProfileRoute = () => {
  const navigate = useNavigate();
  
  const navigateHome = () => navigate('/');
  const navigateToPage = (page) => navigate(`/${page}`);

  return <Profile navigateHome={navigateHome} navigateToPage={navigateToPage} />;
};

export const AdminRoute = () => {
  const navigate = useNavigate();
  const navigateHome = () => navigate('/');

  return <AdminPanel navigateHome={navigateHome} />;
};

export const AboutRoute = () => {
  const navigate = useNavigate();
  
  return (
    <SharedPageLayout currentPage="about">
      {/* Hero Banner - Background Image with Blue Overlay */}
      <div className="relative text-white overflow-hidden">
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bdaed582-c4d0-4d99-8e9e-aa6015fcd0f5/artifacts/f4re32p1_about-us-cover-3.jpg)' }}
        ></div>
        
        {/* Blue overlay to match brand colors */}
        <div className="absolute inset-0 bg-[#0007b5]/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-20 md:py-28">
            <div className="max-w-4xl">
              <p className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-6">Our Story</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-heading mb-6">
                About Us
              </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-2xl leading-relaxed">
                Engineers passionate about turning ideas into reality through precision 3D printing
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Mission Statement with Visual */}
        <section className="mb-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-12 items-center mb-8">
              <div className="lg:col-span-3 rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modelcraft-38/artifacts/pmf7iszc_About%20Us%20-%20Parts%20Showcase%202.jpg" 
                  alt="3D Printed Parts Showcase"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="lg:col-span-2">
                <p className="text-lg text-slate-700 leading-relaxed mb-6">
                  <span className="text-[1.05em] text-slate-800 font-black tracking-wide font-heading">CREATE 3D WORKS</span> is more than just a 3D printing company - it is born from hands-on expertise and passion for turning ideas into reality. Backed by the experience of a team of engineers and techs who honed their skills as a "skunkworks" 3D printing team, our roots run deep in solving real-world challenges through advanced additive manufacturing.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  With a combined 8 years of specialized 3D printing experience and over 20 years in engineering and design experience, our team has mastered the full spectrum of part creation specifically for 3D Printing.
                </p>
              </div>
            </div>
            
            <div className="mb-8">
              <p className="text-lg text-slate-600 leading-relaxed">
                We've designed, prototyped, tested, and produced 3D printed components for diverse applications, from cosmetic and display pieces to functional and final-use parts leading to 3,000+ lb. of material (filament) printed.
              </p>
            </div>
            
            <div className="my-8">
              <div className="relative flex p-6">
                {/* Top-left corner accent */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#0007b5]/20 rounded-tl-lg"></div>
                {/* Bottom-right corner accent */}
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#0007b5]/20 rounded-br-lg"></div>
                
                <div className="flex-shrink-0 mr-4">
                  <span className="text-[1.05em] text-[#0007b5] font-black tracking-wide font-heading">OUR MISSION:</span>
                  <div className="w-16 h-px bg-[#0007b5]/20 mt-0.5"></div>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Combine engineering expertise with manufacturing flexibility to deliver results you can trust. Whether we produce your already designed parts, design your components from scratch, or anywhere in between; we don't just print - we can analyze, optimize, and engineer solutions tailored to your specific requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="mb-20">
          <div className="relative rounded-3xl p-12 text-white overflow-hidden">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: 'url(/by_the_numbers_background.jpg)' }}
            ></div>
            {/* Brand Blue Overlay (80% opaque) */}
            <div className="absolute inset-0 bg-[#0007b5] opacity-80"></div>
            
            {/* Content */}
            <div className="relative z-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-4">By the Numbers</h2>
                <p className="text-blue-100 text-lg">Real experience you can count on</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">8+ Years</div>
                  <div className="text-blue-200">Printing Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">3,000+ lb</div>
                  <div className="text-blue-200">Material Printed</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">15+</div>
                  <div className="text-blue-200">Use Cases Covered</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">100%</div>
                  <div className="text-blue-200">In-house Production</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values - Unique Editorial Layout */}
        <section className="mb-20">
          <div className="mb-16">
            <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">Our Philosophy</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-2 mb-6">What Drives Us</h2>
            <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
              Our experience stemmed from in-house projects where we managed every stage—from initial concept and design development to rigorous testing, iterative revisions, and seamless production.
            </p>
          </div>
          
          {/* Staggered asymmetric layout */}
          <div className="relative">
            {/* Top row - Engineering First (large) + Quality Obsessed */}
            <div className="grid lg:grid-cols-12 gap-6 mb-6">
              {/* Engineering First - White card with blue accent on left */}
              <div className="lg:col-span-7 group">
                <div className="bg-white rounded-3xl p-10 h-full border-2 border-slate-100 hover:border-[#0007b5]/30 transition-all duration-300 relative overflow-hidden">
                  {/* Left accent */}
                  <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#0007b5]"></div>
                  
                  <div className="relative z-10 pl-4">
                    <span className="text-7xl font-black text-slate-100 mb-6 block">01</span>
                    <h3 className="text-3xl font-bold text-slate-900 mb-4">Engineering First</h3>
                    <p className="text-slate-600 text-lg leading-relaxed max-w-md">
                      We approach every project with an engineer's mindset—analyzing requirements, 
                      optimizing designs, and selecting the right materials for your specific application.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Quality Obsessed - Clean white card, no accent */}
              <div className="lg:col-span-5 group">
                <div className="bg-white rounded-3xl p-8 h-full border-2 border-slate-100 hover:border-slate-200 transition-all duration-300 relative overflow-hidden">
                  <div className="relative z-10">
                    <span className="text-6xl font-black text-slate-100 mb-5 block">02</span>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Quality Obsessed</h3>
                    <p className="text-slate-600 leading-relaxed">
                      Every part we design undergoes rigorous quality checks. From design, to real stress testing, to finished components—your project's success depends on performance and reliability.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bottom row - Innovation + Solutions cards */}
            <div className="grid lg:grid-cols-12 gap-6">
              {/* Innovation Driven - Clean white card, no accent */}
              <div className="lg:col-span-6 group">
                <div className="bg-white rounded-3xl p-8 h-full border-2 border-slate-100 hover:border-slate-200 transition-all duration-300 relative overflow-hidden">
                  <div className="relative z-10">
                    <span className="text-6xl font-black text-slate-100 mb-5 block">03</span>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Innovation Driven</h3>
                    <p className="text-slate-600 leading-relaxed">
                      We continuously explore new materials, techniques, and technologies to provide 
                      cutting-edge solutions that push the boundaries of what's possible with 3D printing.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Solutions that Work - White card with blue accent on right */}
              <div className="lg:col-span-6 group">
                <div className="bg-white rounded-3xl p-8 h-full border-2 border-slate-100 hover:border-[#0007b5]/30 transition-all duration-300 relative overflow-hidden">
                  {/* Right accent */}
                  <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-[#0007b5]"></div>
                  
                  <div className="relative z-10 pr-4">
                    <span className="text-6xl font-black text-slate-100 mb-5 block">04</span>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Solutions that Work</h3>
                    <p className="text-slate-600 leading-relaxed">
                      We don't stop at printing a part that fits. 3D Printing isn't the solution—the printed part is. Our commitment is to optimize the material selection and part design, configuration, and structure based on the project requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Expertise - Redesigned Clean Layout */}
        <section className="mb-20 py-16 bg-slate-50" style={{marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)', paddingLeft: 'calc(50vw - 50%)', paddingRight: 'calc(50vw - 50%)'}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Content Side - Left */}
              <div>
                <div className="mb-2">
                  <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">What We Do</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Expertise</h2>
                <p className="text-lg text-slate-600 mb-8">
                  Prototype, functional, or display: 3D printed parts that Work.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-5 h-5 text-[#0007b5]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Prototype Development</h3>
                      <p className="text-slate-600 text-sm">Rapid iteration and testing with engineering analysis to optimize designs for functionality, fitment, and production readiness.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-5 h-5 text-[#0007b5]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Production Components</h3>
                      <p className="text-slate-600 text-sm">Load-bearing, functional parts with engineering validation and precise material selection for specific performance and durability requirements.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-5 h-5 text-[#0007b5]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Cosmetic & Display</h3>
                      <p className="text-slate-600 text-sm">Smoothed, painted, and professionally finished components for presentations, trade shows, and high-end visual applications.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-5 h-5 text-[#0007b5]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Engineering Consultation</h3>
                      <p className="text-slate-600 text-sm">Comprehensive design optimization, material selection guidance, and manufacturing consultation based on years of real-world experience.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Image Side - Right */}
              <div className="relative">
                {/* Subtle accent behind image */}
                <div className="absolute -inset-3 bg-gradient-to-br from-[#0007b5]/10 to-blue-200/20 rounded-3xl -z-10"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_modelcraft-38/artifacts/9oy2u529_About%20us%20feature.jpg" 
                  alt="3D Printed Part with CAD Design"
                  className="w-full h-auto rounded-2xl scale-105 shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-20">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center justify-center gap-3">
                Why <img src="https://customer-assets.emergentagent.com/job_4d5d21e8-e723-4374-8b92-bc54b14f6d43/artifacts/kg4derrn_Asset%202%403x%20%28text%29.png" alt="CREATE 3D Works" className="h-[72px] inline-block" />
              </h2>
              <div className="w-40 h-0.5 bg-[#0007b5] mt-4"></div>
            </div>
            <p className="text-lg text-slate-600 leading-relaxed mb-8 text-center">
              Our expertise spans every stage of 3D production—from design and prototyping to testing, revisions, and finishing—equipping us to support your project needs at every stage. We understand how each phase integrates into the whole process, providing seamless, informed solutions that reduce risk and improve efficiency for your projects.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Fast Turnaround Times</h3>
                  <p className="text-slate-600">Competitive pricing without compromising on quality or speed</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Expert Support</h3>
                  <p className="text-slate-600">Guidance throughout your entire project lifecycle</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Confidential Projects</h3>
                  <p className="text-slate-600">Secure handling with NDA protection for sensitive work</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Real-World Experience</h3>
                  <p className="text-slate-600">3,000+ lb. of production across a broad-spectrum of applications and use cases</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-12 text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Experience the difference that engineering expertise makes. Get an instant quote 
            or contact us to discuss your project requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const quoteSection = document.querySelector('#instant-quote');
                  if (quoteSection) {
                    quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Instant Quote
            </button>
            <button
              onClick={() => {
                navigate('/contact');
                setTimeout(() => {
                  const helpSection = document.querySelector('#here-to-help');
                  if (helpSection) {
                    const yOffset = -80;
                    const y = helpSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="border border-blue-200 text-white hover:bg-blue-800 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us
            </button>
          </div>
        </section>

      </div>
    </SharedPageLayout>
  );
};
